import {skipToken} from '@reduxjs/toolkit/query'
import compose from 'lodash/flowRight'
import * as React from 'react'
import {useContext} from 'react'
import type {Location, PathMatch} from 'react-router-dom'
import {useParams, useLocation, useNavigate, useSearchParams} from 'react-router-dom'

import {ProjectsTreeContext} from '../components/common/ProjectsTree/ProjectsTree.context'
import {getBuildOrPermalinkLocator} from '../components/pages/BuildPage/BuildPage.utils'
import {EntityContext} from '../contexts/entity'
import {useDerivedState} from '../hocs/withDerivedState'
import withHook from '../hocs/withHook'
import {mergeIfDifferent} from '../reducers/utils'
import {getSingleBuildArg} from '../rest/builds'
import Routes, {
  ALL_PROJECTS_HASH,
  browserHistory,
  getBuildHref,
  getFavoriteBuildsHref,
  getFavoriteProjectsHref,
  getHrefWithQueryParams,
  matchRoute,
} from '../routes'
import {getBuild} from '../selectors'
import type {Branch} from '../services/rest'
import {getIdFromLocator, restApi} from '../services/rest'
import type {
  AgentId,
  AgentPoolId,
  AgentTypeId,
  BuildId,
  BuildTypeId,
  ChangeId,
  Enhancer,
  HOC,
  ProjectId,
  ProjectOrBuildTypeNode,
  TestId,
} from '../types'
import {
  getProjectBuildTypeFilter,
  toAgentId,
  toAgentPoolId,
  toAgentTypeId,
  toBuildId,
  toBuildTypeId,
  toChangeId,
  toCustomSidebarItemId,
  toProjectId,
  toTestId,
} from '../types'
import type {SidebarActiveItem} from '../types/projectTrees'
import {parseBranch} from '../utils/branchNames'
import type {QueryParams} from '../utils/queryParams'
import {objectToQuery, queryToObject, searchParamsToObject} from '../utils/queryParams'
import type {UserProperty} from '../utils/userProperties'

import {useSetUserProperty} from './useSetUserProperty'
import {useUserProperty} from './useUserProperty'

import {useAppSelector} from 'src/hooks/react-redux'

export function useBuildHref(buildId: BuildId | null | undefined, isAllProjects?: boolean) {
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  return buildId != null ? getBuildHref(buildId, buildTypeId, isAllProjects) : null
}
export function useBuildHrefWithSaveParams(
  location: Location,
  buildId: BuildId | null | undefined,
  savedParams: ReadonlyArray<string>,
  isAllProjects = false,
): string {
  const href = useBuildHref(buildId, isAllProjects)
  return getHrefWithQueryParams(location, href, params =>
    savedParams.reduce(
      (acc, paramName) =>
        params[paramName] != null ? {...acc, [paramName]: String(params[paramName])} : acc,
      {},
    ),
  )
}
type SyncedQueryParamArg = {
  param: string
  userProperty: UserProperty
  values?: ReadonlyArray<string>
  defaultValue?: string | null
  skip?: boolean
}

const useLocationSelector = <T>(selector: (location: Location) => T): T => selector(useLocation())

const useMatchSelector = <T>(route: string, selector: (match: PathMatch | null) => T): T =>
  useLocationSelector(location => selector(matchRoute(route, location.pathname)))

export const getProjectIdFromUrl = (pathname: string): ProjectId | undefined => {
  const matched = matchRoute(Routes.PROJECT, pathname)
  const id = matched?.params.projectId
  return id != null ? toProjectId(id) : undefined
}

export const useProjectId = (): ProjectId | undefined => {
  const {projectId} = React.useContext(EntityContext)
  const location = useLocation()
  const fromUrl = getProjectIdFromUrl(location.pathname)
  return projectId ?? fromUrl
}
type ProjectProps = {
  readonly projectId: ProjectId
}
export const withProjectId: Enhancer<ProjectProps | null | undefined, any> = withHook(() => {
  const projectId = useProjectId()
  return projectId != null
    ? {
        projectId,
      }
    : null
})

export const getBuildTypeIdFromUrl = (pathname: string): BuildTypeId | null | undefined => {
  const matched = matchRoute(Routes.BUILD_TYPE, pathname) ?? matchRoute(Routes.BUILD, pathname)
  const id = matched?.params.buildTypeId
  return id != null ? toBuildTypeId(id) : undefined
}

export const useBuildTypeId = (): BuildTypeId | null | undefined => {
  const {buildTypeId} = React.useContext(EntityContext)
  const location = useLocation()
  const fromUrl = getBuildTypeIdFromUrl(location.pathname)
  return buildTypeId ?? fromUrl
}

const usePipelineId = (): BuildTypeId | null | undefined => {
  const {pipelineId} = React.useContext(EntityContext)
  const {pipelineId: urlPipelineId} = useParams()

  return pipelineId ?? urlPipelineId
}

type BuildTypeProps = {
  buildTypeId: BuildTypeId
}
export const withBuildTypeId: Enhancer<BuildTypeProps | null | undefined, any> = withHook(() => {
  const buildTypeId = useBuildTypeId()
  return buildTypeId != null
    ? {
        buildTypeId,
      }
    : null
})
export const useQueryParams = () => {
  const [params] = useSearchParams()
  return searchParamsToObject(params)
}
export const useQueryParam = (param: string, defaultValue?: string | null | undefined) =>
  useQueryParams()[param] ?? defaultValue
export const useBranch = (): Branch | null | undefined => parseBranch(useQueryParam('branch'))
export const useBuildIdFromPath = (): BuildId | null | undefined => {
  const branch = useBranch()
  const buildLocator = useMatchSelector(Routes.BUILD, matched => {
    const {buildId, buildTypeId} = matched?.params ?? {}
    return getBuildOrPermalinkLocator(buildId, buildTypeId, branch)
  })

  const fromBuildUnknownBuildTypePath = useMatchSelector(
    Routes.BUILD_UNKNOWN_BUILDTYPE,
    matched => {
      const id = matched?.params.buildId
      return id != null ? toBuildId(id) : undefined
    },
  )

  const {actualBuildId} = restApi.endpoints.getBuildNormalizedAsList.useQuery(
    buildLocator ? getSingleBuildArg(buildLocator) : skipToken,
    {
      selectFromResult: ({data}) => ({
        actualBuildId: data?.result[0],
      }),
    },
  )

  return actualBuildId || toBuildId(getIdFromLocator(buildLocator)) || fromBuildUnknownBuildTypePath
}
export const useBuildId = (): BuildId | null | undefined => {
  const {buildId} = React.useContext(EntityContext)
  const fromPath = useBuildIdFromPath()
  return buildId ?? fromPath
}
type BuildProps = {
  readonly buildId: BuildId
}
export const withBuildId: Enhancer<BuildProps | null | undefined, any> = withHook(() => {
  const buildId = useBuildId()
  return buildId != null
    ? {
        buildId,
      }
    : null
})
export const getAgentIdFromUrl = (pathname: string): AgentId | null | undefined => {
  const matched = matchRoute(Routes.AGENT, pathname)
  const id = matched?.params.agentId
  return id != null ? toAgentId(id) : undefined
}
export const useAgentId = (): AgentId | null | undefined => {
  const {agentId} = React.useContext(EntityContext)
  const location = useLocation()
  const fromUrl = getAgentIdFromUrl(location.pathname)
  return agentId ?? fromUrl
}
const withAgentId = withHook<
  | {
      readonly agentId: AgentId
    }
  | null
  | undefined,
  any
>(() => {
  const agentId = useAgentId()
  return agentId != null
    ? {
        agentId,
      }
    : null
})

export const getAgentPoolIdFromUrl = (pathname: string): AgentPoolId | null | undefined => {
  const matched = matchRoute(Routes.AGENT_POOL, pathname)
  const id = matched?.params.agentPoolId
  return id != null ? toAgentPoolId(id) : undefined
}
export const useAgentPoolId = (): AgentPoolId | null | undefined => {
  const {agentPoolId} = React.useContext(EntityContext)
  const location = useLocation()
  const fromUrl = getAgentPoolIdFromUrl(location.pathname)
  return agentPoolId ?? fromUrl
}
const withAgentPoolId = withHook<
  | {
      readonly agentPoolId: AgentPoolId
    }
  | null
  | undefined,
  any
>(() => {
  const agentPoolId = useAgentPoolId()
  return agentPoolId != null
    ? {
        agentPoolId,
      }
    : null
})
export const getAgentTypeIdFromUrl = (pathname: string): AgentTypeId | null | undefined => {
  const matched = matchRoute(Routes.CLOUD_IMAGE, pathname)
  const id = matched?.params.agentTypeId
  return id != null ? toAgentTypeId(id) : undefined
}
export const useAgentTypeId = (): AgentTypeId | null | undefined => {
  const {agentTypeId} = React.useContext(EntityContext)
  const location = useLocation()
  const fromUrl = getAgentTypeIdFromUrl(location.pathname)
  return agentTypeId ?? fromUrl
}
const withAgentTypeId = withHook<
  | {
      readonly agentTypeId: AgentTypeId
    }
  | null
  | undefined,
  any
>(() => {
  const agentTypeId = useAgentTypeId()
  return agentTypeId != null
    ? {
        agentTypeId,
      }
    : null
})

export const withActiveEntityId = compose(
  withProjectId,
  withBuildTypeId,
  withBuildId,
  withAgentId,
  withAgentPoolId,
  withAgentTypeId,
)

export function useProjectOrBuildTypeNode(): ProjectOrBuildTypeNode | null {
  const projectId = useProjectId()
  const buildTypeId = useBuildTypeId()
  const pipelineId = usePipelineId()
  return React.useMemo(
    () =>
      getProjectBuildTypeFilter({
        projectId,
        buildTypeId,
        pipelineId,
      }),
    [buildTypeId, projectId, pipelineId],
  )
}

export const withProjectOrBuildTypeNode = withHook(() => ({
  projectOrBuildTypeNode: useProjectOrBuildTypeNode(),
}))
export function useProjectOrBuildTypeNodeFromUrl(): ProjectOrBuildTypeNode | null | undefined {
  const location = useLocation()
  const projectId = getProjectIdFromUrl(location.pathname)
  const buildTypeId = getBuildTypeIdFromUrl(location.pathname)
  return React.useMemo(
    () =>
      getProjectBuildTypeFilter({
        projectId,
        buildTypeId,
      }),
    [buildTypeId, projectId],
  )
}
export const useIsOverviewLinkSelected = (): boolean =>
  useMatchSelector(Routes.FAVORITE_PROJECTS, matched => matched != null)

export const getIsFavoriteLinkSelected = (pathname: string): boolean =>
  matchRoute(Routes.FAVORITE_BUILDS, pathname) != null

const useIsAgentSelected = () => useMatchSelector(Routes.AGENT, matched => matched != null)

export const getIsAgentsRootSelected = (pathname: string): boolean =>
  matchRoute(Routes.AGENTS, pathname) != null

const useIsAgentsPoolSelected = () =>
  useMatchSelector(Routes.AGENT_POOL, matched => matched != null)

export const getIsUnauthorizedAgentsSelected = (pathname: string): boolean =>
  matchRoute(Routes.AGENTS_UNAUTHORIZED, pathname) != null
export const getIsAgentsOverviewSelected = (pathname: string): boolean =>
  matchRoute(Routes.AGENTS_OVERVIEW, pathname) != null

export const getIsFavoriteAgentPoolsSelected = (pathname: string): boolean =>
  matchRoute(Routes.FAVORITE_AGENT_POOLS, pathname) != null

const useIsCloudImageSelected = () =>
  useMatchSelector(Routes.CLOUD_IMAGE, matched => matched != null)

export const useIsAgentsPartSelected = (): boolean => {
  const location = useLocation()
  const agent = useIsAgentSelected()
  const overview = getIsAgentsOverviewSelected(location.pathname)
  const unauthorizedAgents = getIsUnauthorizedAgentsSelected(location.pathname)
  const root = getIsAgentsRootSelected(location.pathname)
  const pool = useIsAgentsPoolSelected()
  const cloud = useIsCloudImageSelected()
  const favoriteAgentPools = getIsFavoriteAgentPoolsSelected(location.pathname)
  return agent || overview || unauthorizedAgents || root || pool || cloud || favoriteAgentPools
}
export const getIsQueueSelected = (pathname: string): boolean =>
  matchRoute(Routes.QUEUE, pathname) != null
export const useIsGuidesOverviewSelected = (): boolean =>
  useMatchSelector(Routes.GUIDES, matched => matched != null)
export const getTestId = (pathname: string): TestId | null | undefined => {
  const matched = matchRoute(Routes.TEST, pathname)
  const id = matched?.params.testId
  return id != null ? toTestId(id) : undefined
}

export const getChangeId = (pathname: string): ChangeId | null | undefined => {
  const matched = matchRoute(Routes.CHANGE, pathname)
  const id = matched?.params.changeId
  return id != null ? toChangeId(id) : undefined
}

export const getIsChangesSelected = (pathname: string): boolean =>
  matchRoute(Routes.CHANGES, pathname) != null

export const getIsInvestigationsSelected = (pathname: string): boolean =>
  matchRoute(Routes.INVESTIGATIONS, pathname) != null

export const useIsPipelinesSelected = (): boolean =>
  useMatchSelector(Routes.PIPELINES, matched => matched != null)

export function usePrevLocation() {
  const location = useLocation()
  const prevLocation = useDerivedState(location, prevState =>
    browserHistory.createHref(location) !== browserHistory.createHref(prevState)
      ? location
      : prevState,
  )
  return browserHistory.createHref(prevLocation) !== browserHistory.createHref(location)
    ? prevLocation
    : null
}
export const useBooleanQueryParam = (param: string): boolean => {
  const stringValue = useQueryParam(param)
  return stringValue === 'true'
}
export const withQueryParam: (arg0: string) => HOC<any, any> = param =>
  withHook(() => ({
    [param]: useQueryParam(param),
  }))
type SetQueryParams = (
  params: (QueryParams | null | undefined) | ((prevParams: QueryParams) => QueryParams),
  replace?: boolean,
) => unknown
export const useSetQueryParams = (): SetQueryParams => {
  const location = useLocation()
  const navigate = useNavigate()
  return React.useCallback(
    (value, replace = true) => {
      const queryParams = queryToObject(location.search)
      const to = {
        hash: location.hash,
        search: objectToQuery(
          typeof value === 'function' ? value(queryParams) : mergeIfDifferent(queryParams, value),
        ),
      }
      navigate(to, {replace})
    },
    [location.hash, location.search, navigate],
  )
}
export type QueryParam = string | null | undefined
export type SetQueryParam = (value: QueryParam, replace?: boolean) => unknown
export const useSetQueryParam = (param: string, defaultValue?: QueryParam): SetQueryParam => {
  const setQueryParams = useSetQueryParams()
  return React.useCallback(
    (value, replace) =>
      setQueryParams(
        {
          [param]: value === defaultValue ? null : value,
        },
        replace,
      ),
    [defaultValue, param, setQueryParams],
  )
}
type SetBooleanQueryParam = (value: boolean, replace?: boolean) => unknown

function useSetBooleanQueryParam(param: string, defaultTrue = false): SetBooleanQueryParam {
  const setQueryParam = useSetQueryParam(param, String(defaultTrue))
  return React.useCallback(
    (
      value,
      replace, // omit param if its value is default
    ) => setQueryParam(String(value), replace),
    [setQueryParam],
  )
}

export const useQueryParamState = (
  param: string,
  defaultValue?: QueryParam,
): [QueryParam, SetQueryParam] => [
  useQueryParam(param, defaultValue),
  useSetQueryParam(param, defaultValue),
]
export const useBooleanQueryParamState = (
  param: string,
  defaultValue?: boolean,
): [boolean, SetBooleanQueryParam] => [
  useBooleanQueryParam(param),
  useSetBooleanQueryParam(param, defaultValue),
]
export const useHash = (): string => useLocationSelector(location => location.hash.slice(1))
type SetHash = (value: string, replace?: boolean) => unknown
export const useSetHash = (): SetHash => {
  const location = useLocation()
  const navigate = useNavigate()
  return React.useCallback(
    (value, replace = true) =>
      navigate(getHrefWithQueryParams(location, null, undefined, value), {
        replace,
      }),
    [location, navigate],
  )
}
export type BranchProps = {
  branch?: Branch | null
}
export const withBranchProps: Enhancer<BranchProps, any> = withHook(() => {
  const branchQueryParam = useQueryParam('branch')
  const branch = parseBranch(branchQueryParam)
  return branchQueryParam !== undefined
    ? {
        branch, // null - all branches
      }
    : {} // undefined - branch not specified
})
const favoriteBuildsItem = {
  nodeType: 'link',
  id: toCustomSidebarItemId(getFavoriteBuildsHref()),
} as const
const overviewProjectsItem = {
  nodeType: 'link',
  id: toCustomSidebarItemId(getFavoriteProjectsHref()),
} as const
export const withActivatedByUrlItem = withHook(() => {
  const location = useLocation()
  const isFavoriteLinkSelected = getIsFavoriteLinkSelected(location.pathname)
  const isOverviewLinkSelected = useIsOverviewLinkSelected()
  const projectOrBuildTypeNode = useProjectOrBuildTypeNode()
  const hash = useHash()
  const isAllProjects = hash === ALL_PROJECTS_HASH
  const {currentId, currentNodeType} = useContext(ProjectsTreeContext)
  const activeItem: SidebarActiveItem | null = React.useMemo(() => {
    if (currentId != null && currentNodeType != null) {
      return {
        id: currentId,
        nodeType: currentNodeType,
        group: isAllProjects ? 'all' : 'favorites',
      }
    }

    if (isFavoriteLinkSelected) {
      return favoriteBuildsItem
    }

    if (isOverviewLinkSelected) {
      return overviewProjectsItem
    }

    return (
      projectOrBuildTypeNode && {
        ...projectOrBuildTypeNode,
        group: isAllProjects ? 'all' : 'favorites',
      }
    )
  }, [
    currentId,
    currentNodeType,
    isAllProjects,
    isFavoriteLinkSelected,
    isOverviewLinkSelected,
    projectOrBuildTypeNode,
  ])

  return {
    activeItem,
  }
})

const defaultSyncedQueryValues: Record<string, string | undefined> = {}

function validateDefaultSyncedQueryValue(userProperty: UserProperty, value: string | undefined) {
  const existingDefaultValue = defaultSyncedQueryValues[userProperty]

  if (existingDefaultValue == null) {
    defaultSyncedQueryValues[userProperty] = value
  } else if (existingDefaultValue !== value) {
    // eslint-disable-next-line no-console
    console.warn(
      `Warning: The default value for the user property '${userProperty}' is being changed from '${existingDefaultValue}' to '${value}'. This may cause unexpected behavior.`,
    )
  }

  return defaultSyncedQueryValues[userProperty]
}

export function useSyncedQueryParam(
  params: SyncedQueryParamArg,
): [string | undefined, (newValue: string, skipNavigate?: boolean) => unknown] {
  const {param, userProperty, values, skip} = params
  const validate = (value: string | null | undefined) =>
    values != null ? values.find(item => item === value) : value

  const defaultValue = params.defaultValue ?? undefined
  const [queryValue, setQueryValue] = useQueryParamState(param)
  const validatedQueryValue = validate(queryValue)
  const userValue = useUserProperty(userProperty)
  const validatedUserValue = validate(userValue)
  const validatedDefaultValue =
    skip || process.env.NODE_ENV === 'test'
      ? defaultValue
      : validateDefaultSyncedQueryValue(userProperty, defaultValue)
  const value = validatedQueryValue ?? validatedUserValue ?? validatedDefaultValue ?? values?.[0]

  React.useEffect(() => {
    // TODO remove when https://github.com/remix-run/react-router/pull/8851 is merged
    if (skip || process.env.NODE_ENV === 'test') {
      return
    }

    const desiredQueryValue = value === validatedDefaultValue ? undefined : value
    if (queryValue !== desiredQueryValue) {
      setQueryValue(desiredQueryValue)
    }
  }, [value, queryValue, setQueryValue, skip, validatedDefaultValue])
  const setUserProperty = useSetUserProperty()
  return [
    value,
    React.useCallback(
      (newValue: string, skipNavigate = false) => {
        if (!skipNavigate) {
          setQueryValue(newValue)
        }

        setUserProperty(userProperty, newValue)
      },
      [setUserProperty, userProperty, setQueryValue],
    ),
  ]
}

type SyncedBooleanQueryParamArg = {
  readonly param: string
  readonly userProperty: UserProperty
  readonly defaultTrue?: boolean
  readonly skip?: boolean
}
export function useSyncedBooleanQueryParam({
  param,
  userProperty,
  defaultTrue = false,
  skip,
}: SyncedBooleanQueryParamArg): [boolean, (newValue: boolean, skipNavigate?: boolean) => unknown] {
  const [value, setValue] = useSyncedQueryParam({
    param,
    userProperty,
    values: ['true', 'false'],
    defaultValue: String(defaultTrue),
    skip,
  })
  return [
    value === 'true',
    React.useCallback(
      (newValue, skipNavigate) => setValue(String(newValue), skipNavigate),
      [setValue],
    ),
  ]
}

export const useAgentPoolIdParam = () => {
  const activeAgentPoolId = useQueryParam('agentPoolId')
  return activeAgentPoolId != null ? toAgentPoolId(activeAgentPoolId) : undefined
}
